import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { LOGO, CALENDLY } from "@/data";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Dark navbar on:
  // - the entire /openai-partner page, or
  // - the very top of the landing page (over the dark OpenAI masthead)
  const isPartner = pathname.startsWith("/openai-partner");
  const dark = isPartner || (pathname === "/" && !scrolled);

  const links = [
    { label: "Services", href: "/#services", type: "hash" },
    { label: "Results", href: "/#results", type: "hash" },
    { label: "About", href: "/#about", type: "hash" },
    { label: "OpenAI Partner", href: "/openai-partner", type: "route", highlight: true },
    { label: "Process", href: "/#process", type: "hash" },
  ];

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        dark
          ? "bg-[#050505]/80 backdrop-blur-xl border-b border-white/10"
          : "bg-[#FDFCF8]/80 backdrop-blur-xl border-b border-black/5"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center">
          <img src={LOGO} alt="DNA Digital" className="h-11 w-auto object-contain" />
        </Link>
        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => {
            const cls = l.highlight
              ? "font-body text-sm font-bold tracking-wide text-[#34d399] hover:text-white transition-colors"
              : `font-body text-sm font-semibold tracking-wide ${dark ? "text-white/80 hover:text-white" : "text-black/70 hover:text-black"} link-underline`;
            return l.type === "route" ? (
              <Link key={l.href} to={l.href} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className={cls}>
                {l.label}
              </Link>
            ) : (
              <a key={l.href} href={l.href} data-testid={`nav-${l.label.toLowerCase()}`} className={cls}>
                {l.label}
              </a>
            );
          })}
        </nav>
        <div className="flex items-center gap-3">
          <Link
            to="/openai-partner"
            data-testid="nav-openai-partner-mobile"
            className="md:hidden inline-flex items-center gap-1.5 rounded-full border border-[#10a37f]/50 bg-[#10a37f]/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-[#34d399]"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#34d399] animate-pulse" />
            OpenAI
          </Link>
          <a
            href={CALENDLY}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-cta-button"
            className="group relative inline-flex items-center gap-2 rounded-full bg-[#0A0A0A] px-5 py-2.5 text-sm font-semibold text-white font-body overflow-hidden"
          >
            <span className="relative z-10"><span className="sm:hidden">Call</span><span className="hidden sm:inline">Book a Call</span></span>
            <span className="absolute inset-0 z-0 translate-y-full bg-[#F48220] transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:translate-y-0" />
            <span className="relative z-10 h-2 w-2 rounded-full bg-[#E70D71]" />
          </a>
        </div>
      </div>
    </motion.header>
  );
}
